import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import t from '../theme';
import { useAuth } from '../context/AuthContext';

export default function LessonDetailPage() {
  const { classId, lessonId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const fileInputRef = useRef(null);

  // isClassLesson = came from /classes/:classId/lessons/:lessonId
  const isClassLesson = !!classId;
  const apiBase = isClassLesson
    ? `/api/classes/${classId}/lessons/${lessonId}`
    : `/api/lessons/${lessonId}`;
  const backPath = isClassLesson ? `/classes/${classId}` : '/lessons';

  const [lesson, setLesson] = useState(null);
  const [taals, setTaals] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [comment, setComment] = useState('');
  const [commenting, setCommenting] = useState(false);

  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [removingMedia, setRemovingMedia] = useState(false);

  const fetchLesson = useCallback(async () => {
    try {
      const [lessonRes, taalsRes, typesRes] = await Promise.all([
        axios.get(apiBase),
        axios.get('/api/taals'),
        axios.get('/api/types'),
      ]);
      setLesson(lessonRes.data);
      setTaals(taalsRes.data);
      setTypes(typesRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { fetchLesson(); }, [fetchLesson]);

  // ── Upload ─────────────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    setUploadFile(e.target.files[0] || null);
    setUploadError('');
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadError('');
    const formData = new FormData();
    formData.append('media', uploadFile);
    try {
      await axios.post(`${apiBase}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: e => setUploadProgress(Math.round((e.loaded * 100) / e.total)),
      });
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchLesson();
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Upload failed.');
    } finally { setUploading(false); setUploadProgress(0); }
  };

  const handleRemoveMedia = async () => {
    if (!window.confirm('Remove this media file?')) return;
    setRemovingMedia(true);
    try {
      await axios.delete(`${apiBase}/media`);
      fetchLesson();
    } catch (err) { console.error(err); }
    finally { setRemovingMedia(false); }
  };

  // ── Comments ───────────────────────────────────────────────────────────────
  const handleComment = async () => {
    if (!comment.trim()) return;
    setCommenting(true);
    try {
      await axios.post(`${apiBase}/comments`, { comment_text: comment.trim() });
      setComment('');
      fetchLesson();
    } catch (err) { console.error(err); }
    finally { setCommenting(false); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${apiBase}/comments/${commentId}`);
      fetchLesson();
    } catch (err) { console.error(err); }
  };

  // ── Edit ───────────────────────────────────────────────────────────────────
  const openEdit = () => {
    setEditForm({ title: lesson.title, taal_id: lesson.taal_id, type_id: lesson.type_id, notation: lesson.notation || '' });
    setEditError('');
    setEditModal(true);
  };

  const handleSaveEdit = async () => {
    setEditError('');
    if (!editForm.title.trim()) return setEditError('Title is required.');
    setEditSaving(true);
    try {
      await axios.put(apiBase, editForm);
      setEditModal(false);
      fetchLesson();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to save.');
    } finally { setEditSaving(false); }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const isVideo = url => url && /\.(mp4|mov|avi|mkv|webm)$/i.test(url);
  const isAudio = url => url && /\.(mp3|wav|m4a|aac|ogg)$/i.test(url);
  const formatSize = b => b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;
  const formatTime = d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' at ' + new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  if (loading) return <div style={s.loading}>Loading lesson...</div>;
  if (!lesson) return <div style={s.loading}>Lesson not found.</div>;

  return (
    <div style={s.page}>
      <div style={s.breadcrumb}>
        <button style={s.backBtn} onClick={() => navigate(backPath)}>
          ← {isClassLesson ? 'Back to Class' : 'Back to Lessons'}
        </button>
      </div>

      <div style={s.header}>
        <div style={s.headerLeft}>
          <div style={s.headerMeta}>
            <span style={s.taalBadge}>🥁 {lesson.taal?.name}</span>
            <span style={s.typeBadge}>🎼 {lesson.lessonType?.name}</span>
            {!isClassLesson && (
              <span style={s.standaloneBadge}>📌 Standalone Lesson</span>
            )}
          </div>
          <h1 style={s.title}>{lesson.title}</h1>
        </div>
        {isAdmin && (
          <button style={s.editBtn} onClick={openEdit}>✏️ Edit Details</button>
        )}
      </div>

      <div style={s.contentLayout}>
        {/* Left: media + notation */}
        <div style={s.leftCol}>
          <div style={s.section}>
            <h3 style={s.sectionTitle}>
              🎬 Media
              {lesson.video_url && isAdmin && (
                <button style={s.removeMediaBtn} onClick={handleRemoveMedia} disabled={removingMedia}>
                  {removingMedia ? 'Removing...' : '✕ Remove'}
                </button>
              )}
            </h3>

            {lesson.video_url && isVideo(lesson.video_url) && (
              <video controls style={s.videoPlayer} key={lesson.video_url}>
                <source src={lesson.video_url} />
              </video>
            )}
            {lesson.video_url && isAudio(lesson.video_url) && (
              <div style={s.audioWrap}>
                <div style={s.audioIcon}>🎵</div>
                <audio controls style={s.audioPlayer} key={lesson.video_url}>
                  <source src={lesson.video_url} />
                </audio>
              </div>
            )}

            {isAdmin && (
              <div style={s.uploadArea}>
                <div style={s.uploadDivider}>
                  {lesson.video_url ? '🔄 Replace with a new file' : '📁 Upload a video or audio file'}
                </div>
                <div style={s.uploadRow}>
                  <input ref={fileInputRef} type="file" id="media-upload"
                    accept="video/*,audio/*,.mp4,.mov,.avi,.mkv,.webm,.mp3,.wav,.m4a,.aac,.ogg"
                    onChange={handleFileChange} style={{ display: 'none' }} />
                  <label htmlFor="media-upload" style={s.fileLabel}>
                    {uploadFile ? `📎 ${uploadFile.name} (${formatSize(uploadFile.size)})` : '📂 Choose File'}
                  </label>
                  {uploadFile && (
                    <button style={s.uploadBtn} onClick={handleUpload} disabled={uploading}>
                      {uploading ? `Uploading ${uploadProgress}%` : '⬆ Upload'}
                    </button>
                  )}
                </div>
                {uploading && (
                  <div style={s.progressBar}>
                    <div style={{ ...s.progressFill, width: `${uploadProgress}%` }} />
                  </div>
                )}
                {uploadError && <div style={s.uploadErrorBox}>⚠️ {uploadError}</div>}
                <p style={s.uploadHint}>MP4, MOV, AVI, MKV, WebM, MP3, WAV, M4A, AAC, OGG — up to 500MB</p>
              </div>
            )}

            {!lesson.video_url && !isAdmin && (
              <div style={s.noMedia}>No media uploaded yet.</div>
            )}
          </div>

          {lesson.notation ? (
            <div style={s.section}>
              <h3 style={s.sectionTitle}>📝 Notation & Notes</h3>
              <div style={s.notation}>{lesson.notation}</div>
            </div>
          ) : isAdmin ? (
            <div style={s.noContent}>
              <span>📝</span> No notation yet.
              <button style={s.noContentBtn} onClick={openEdit}>Add notation</button>
            </div>
          ) : null}
        </div>

        {/* Right: comments */}
        <div style={s.rightCol}>
          <div style={s.section}>
            <h3 style={s.sectionTitle}>
              💬 Comments
              <span style={s.commentCount}>{lesson.comments?.length || 0}</span>
            </h3>

            <div style={s.commentInputWrap}>
              <div style={s.commentAvatar}>{user?.username?.[0]?.toUpperCase()}</div>
              <div style={s.commentInputArea}>
                <textarea style={s.commentInput}
                  placeholder="Add a comment or question..."
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  rows={3} />
                <button style={s.commentBtn} onClick={handleComment}
                  disabled={commenting || !comment.trim()}>
                  {commenting ? 'Posting...' : 'Post Comment'}
                </button>
              </div>
            </div>

            <div style={s.commentsList}>
              {!lesson.comments?.length ? (
                <div style={s.noComments}>No comments yet. Be the first!</div>
              ) : [...lesson.comments].reverse().map(c => (
                <div key={c.id} style={s.commentItem}>
                  <div style={{
                    ...s.commentAvatar,
                    background: c.author?.role === 'admin'
                      ? 'linear-gradient(135deg, #f39c12, #e67e22)'
                      : '#c49a3c',
                    flexShrink: 0,
                  }}>
                    {c.author?.username?.[0]?.toUpperCase()}
                  </div>
                  <div style={s.commentContent}>
                    <div style={s.commentHeader}>
                      <span style={s.commentAuthor}>
                        {c.author?.username?.charAt(0).toUpperCase() + c.author?.username?.slice(1)}
                        {c.author?.role === 'admin' && <span style={s.teacherTag}>Teacher</span>}
                      </span>
                      <span style={s.commentTime}>{formatTime(c.createdAt)}</span>
                    </div>
                    <p style={s.commentText}>{c.comment_text}</p>
                    {(isAdmin || c.user_id === user?.id) && (
                      <button style={s.deleteCommentBtn} onClick={() => handleDeleteComment(c.id)}>Delete</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editModal && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && setEditModal(false)}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Edit Lesson</h3>
              <button style={s.closeBtn} onClick={() => setEditModal(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formGrid}>
                <div style={s.fieldGroup}>
                  <label style={s.fieldLabel}>Title *</label>
                  <input style={s.input} value={editForm.title}
                    onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
                </div>
                <div style={s.fieldGroup}>
                  <label style={s.fieldLabel}>Taal</label>
                  <select style={s.select} value={editForm.taal_id}
                    onChange={e => setEditForm(f => ({ ...f, taal_id: e.target.value }))}>
                    {taals.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div style={s.fieldGroup}>
                  <label style={s.fieldLabel}>Type</label>
                  <select style={s.select} value={editForm.type_id}
                    onChange={e => setEditForm(f => ({ ...f, type_id: e.target.value }))}>
                    {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={s.fieldGroup}>
                <label style={s.fieldLabel}>Notation & Notes</label>
                <textarea style={{ ...s.input, resize: 'vertical' }}
                  value={editForm.notation}
                  onChange={e => setEditForm(f => ({ ...f, notation: e.target.value }))}
                  rows={8} placeholder="Enter notation or lesson notes..." />
              </div>
              {editError && <div style={s.errorBox}>⚠️ {editError}</div>}
            </div>
            <div style={s.modalFooter}>
              <button style={s.cancelBtn} onClick={() => setEditModal(false)}>Cancel</button>
              <button style={s.saveBtn} onClick={handleSaveEdit} disabled={editSaving}>
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { maxWidth: '1100px' },
  loading: { padding: '48px', textAlign: 'center', color: '#6b6966' },
  breadcrumb: { marginBottom: '16px' },
  backBtn: { background: 'none', border: 'none', color: '#c49a3c', fontWeight: 700, fontSize: '14px', cursor: 'pointer', padding: 0, fontFamily: 'inherit' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  headerLeft: { flex: 1 },
  headerMeta: { display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' },
  taalBadge: { background: '#1c1b19', color: '#c49a3c', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 700 },
  typeBadge: { background: 'rgba(76,175,125,0.1)', color: '#4caf7d', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 700 },
  standaloneBadge: { background: '#1c1b19', color: '#c49a3c', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 700 },
  title: { fontSize: '26px', fontWeight: 800, color: '#f0ede8', margin: 0, lineHeight: 1.2 },
  editBtn: { background: '#1c1b19', border: '1px solid #f9e79f', color: '#c49a3c', padding: '10px 18px', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  contentLayout: { display: 'grid', gridTemplateColumns: '1fr 360px', gap: '20px', alignItems: 'start' },
  leftCol: { display: 'flex', flexDirection: 'column', gap: '20px' },
  rightCol: { display: 'flex', flexDirection: 'column', gap: '20px' },
  section: { background: '#161614', borderRadius: '16px', border: '1px solid #2a2927', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' },
  sectionTitle: { margin: '0 0 16px', fontSize: '15px', fontWeight: 800, color: '#f0ede8', display: 'flex', alignItems: 'center', gap: '8px' },
  removeMediaBtn: { marginLeft: 'auto', background: 'rgba(224,82,82,0.1)', color: '#e05252', border: 'none', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  videoPlayer: { width: '100%', borderRadius: '12px', background: '#000', maxHeight: '400px', display: 'block' },
  audioWrap: { background: '#1c1b19', borderRadius: '12px', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' },
  audioIcon: { fontSize: '48px' },
  audioPlayer: { width: '100%' },
  uploadArea: { marginTop: '16px', borderTop: '1px dashed #c5e3f7', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' },
  uploadDivider: { fontSize: '13px', color: '#6b6966', fontWeight: 600 },
  uploadRow: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
  fileLabel: { background: '#1c1b19', color: '#c49a3c', border: '2px solid #b8d9f0', borderRadius: '10px', padding: '9px 16px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', flex: 1, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  uploadBtn: { background: '#c49a3c', color: '#161614', border: 'none', borderRadius: '10px', padding: '10px 18px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  progressBar: { height: '6px', background: '#1c1b19', borderRadius: '3px', overflow: 'hidden' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, #5dade2, #2980b9)', borderRadius: '3px', transition: 'width 0.2s' },
  uploadErrorBox: { background: 'rgba(224,82,82,0.1)', border: '1px solid #ffcdd2', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#e05252' },
  uploadHint: { fontSize: '12px', color: '#4a4845', margin: 0 },
  noMedia: { textAlign: 'center', color: '#4a4845', fontSize: '14px', padding: '16px', fontStyle: 'italic' },
  noContent: { background: '#1c1b19', border: '2px dashed #c5e3f7', borderRadius: '12px', padding: '20px', fontSize: '14px', color: '#4a4845', display: 'flex', alignItems: 'center', gap: '10px' },
  noContentBtn: { marginLeft: 'auto', background: '#1c1b19', color: '#c49a3c', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  notation: { background: '#1c1b19', border: '1px solid #2a2927', borderRadius: '10px', padding: '16px', fontSize: '14px', color: '#a8a49e', lineHeight: 1.8, whiteSpace: 'pre-wrap', fontFamily: "'Courier New', monospace" },
  commentCount: { background: '#1c1b19', color: '#c49a3c', borderRadius: '20px', padding: '2px 8px', fontSize: '12px', fontWeight: 700 },
  commentInputWrap: { display: 'flex', gap: '10px', marginBottom: '16px' },
  commentAvatar: { width: '34px', height: '34px', borderRadius: '50%', background: '#c49a3c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 800, color: '#161614', marginTop: '2px' },
  commentInputArea: { flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' },
  commentInput: { padding: '10px 14px', borderRadius: '10px', border: '1px solid #2a2927', fontSize: '14px', color: '#f0ede8', background: '#1c1b19', outline: 'none', fontFamily: 'inherit', resize: 'vertical' },
  commentBtn: { alignSelf: 'flex-end', background: '#c49a3c', color: '#161614', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  commentsList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  noComments: { textAlign: 'center', color: '#4a4845', fontSize: '14px', padding: '16px', fontStyle: 'italic' },
  commentItem: { display: 'flex', gap: '10px' },
  commentContent: { flex: 1, background: '#1c1b19', borderRadius: '10px', padding: '12px', border: '1px solid #2a2927' },
  commentHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' },
  commentAuthor: { fontSize: '13px', fontWeight: 700, color: '#f0ede8', display: 'flex', alignItems: 'center', gap: '5px' },
  teacherTag: { fontSize: '11px', background: '#1c1b19', color: '#c49a3c', padding: '2px 6px', borderRadius: '6px' },
  commentTime: { fontSize: '11px', color: '#4a4845', marginLeft: 'auto' },
  commentText: { margin: 0, fontSize: '14px', color: '#a8a49e', lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  deleteCommentBtn: { background: 'none', border: 'none', color: '#e05252', fontSize: '12px', cursor: 'pointer', padding: '4px 0', fontFamily: 'inherit', marginTop: '6px' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modal: { background: '#161614', borderRadius: '20px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #2a2927' },
  modalTitle: { margin: 0, fontSize: '18px', fontWeight: 800, color: '#f0ede8' },
  closeBtn: { background: '#1c1b19', border: 'none', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontSize: '16px', color: '#c49a3c', fontWeight: 700 },
  modalBody: { padding: '24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 24px', borderTop: '1px solid #2a2927' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '14px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  fieldLabel: { fontSize: '12px', fontWeight: 700, color: '#2e6da4', textTransform: 'uppercase', letterSpacing: '0.3px' },
  input: { padding: '10px 14px', borderRadius: '10px', border: '1px solid #2a2927', fontSize: '14px', color: '#f0ede8', background: '#1c1b19', outline: 'none', fontFamily: 'inherit' },
  select: { padding: '10px 14px', borderRadius: '10px', border: '1px solid #2a2927', fontSize: '14px', color: '#f0ede8', background: '#1c1b19', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' },
  errorBox: { background: 'rgba(224,82,82,0.1)', border: '1px solid #ffcdd2', borderRadius: '10px', padding: '10px 14px', fontSize: '14px', color: '#e05252' },
  cancelBtn: { background: '#1c1b19', color: '#c49a3c', border: '1px solid #b8d9f0', padding: '10px 20px', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  saveBtn: { background: '#c49a3c', color: '#161614', border: 'none', padding: '10px 22px', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' },
};
